import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, CheckSquare } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState, StatusBadge } from "../../components/Common";
import { ADMIN_MENU } from "../../lib/menus";
import { formatIDR, mediaUrl, typeLabel } from "../../lib/format";
import api from "../../lib/api";

export default function AdminProducts() {
  const [rows, setRows] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/products?include_all=true&limit=100").then((r) => { setRows(r.data.items); setSelected([]); }).catch(() => setRows([]));
  useEffect(() => { document.title = "Kelola Produk - DS KODE"; load(); }, []);

  const del = async () => {
    try {
      await api.delete(`/products/${confirm.id}`);
      toast.success("Produk dihapus");
      setConfirm(null);
      load();
    } catch (e) { toast.error("Gagal menghapus"); }
  };

  const toggleSelected = (id) => setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  const allSelected = rows?.length > 0 && selected.length === rows.length;
  const toggleAll = () => setSelected(allSelected ? [] : (rows || []).map((product) => product.id));
  const bulkDelete = async () => {
    if (!selected.length || !window.confirm(`Hapus ${selected.length} produk terpilih?`)) return;
    try {
      const response = await api.post("/products/bulk-delete", { ids: selected });
      toast.success(response.data.message);
      load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Gagal menghapus produk terpilih"); }
  };

  return (
    <DashboardLayout title="Kelola Produk" menu={ADMIN_MENU} testid="admin-products">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-slate-500">{rows ? `${rows.length} produk` : "Memuat..."}</p>
        <div className="flex items-center gap-2">
          {selected.length > 0 && <button onClick={bulkDelete} data-testid="bulk-delete-products" className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700"><Trash2 className="h-4 w-4" /> Hapus Terpilih ({selected.length})</button>}
          <Link to="/admin/products/create" data-testid="add-product-btn" className="btn-primary"><Plus className="h-4 w-4" /> Tambah Produk</Link>
        </div>
      </div>

      {rows === null ? (
        <div className="h-64 skeleton rounded-xl" />
      ) : rows.length === 0 ? (
        <EmptyState icon={Package} title="Belum ada produk." subtitle="Klik Tambah Produk untuk memulai." />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="th w-12"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Pilih semua produk" /></th><th className="th">Produk</th><th className="th">Jenis</th><th className="th">Harga</th><th className="th">Download</th><th className="th">Status</th><th className="th text-right">Aksi</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                    <td className="td"><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelected(p.id)} aria-label={`Pilih ${p.title}`} /></td>
                    <td className="td">
                      <div className="flex items-center gap-3">
                        <img src={mediaUrl(p.thumbnail)} alt="" className="h-10 w-14 rounded object-cover" />
                        <div><p className="font-medium text-slate-900 line-clamp-1">{p.title}</p><p className="text-xs text-slate-400">{p.category_name}</p></div>
                      </div>
                    </td>
                    <td className="td">{typeLabel(p.product_type)}</td>
                    <td className="td font-semibold">{formatIDR(p.price)}</td>
                    <td className="td">{p.download_count}</td>
                    <td className="td"><StatusBadge status={p.status} /></td>
                    <td className="td">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => navigate(`/admin/products/${p.slug}/edit`)} data-testid={`edit-product-${p.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setConfirm(p)} data-testid={`delete-product-${p.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-[100] grid place-items-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={() => setConfirm(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-slate-900">Hapus Produk?</h3>
            <p className="text-sm text-slate-500 mt-1 mb-5">"{confirm.title}" akan dihapus permanen.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirm(null)} className="btn-outline flex-1">Batal</button>
              <button onClick={del} data-testid="confirm-delete" className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-sm">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
